/**
 * Escape closes the drawer on top, and only that one.
 *
 * No panel in this application listened for Escape. That was consistent and
 * consistently wrong: these are modal drawers covering the page, and Escape
 * is the one key everybody tries when a modal has nothing obvious to dismiss
 * it with.
 *
 * **The topmost, and only the topmost**, which is the whole reason this is
 * shared rather than three lines in each component. The attendance month
 * panel can have the day detail open over it, and both are mounted and both
 * are listening: a bare keydown handler in each closes them together, dropping
 * the reader back at the table when they meant to step back one level.
 *
 * So the open drawers keep a stack. Each pushes itself on mount and pops on
 * unmount, and a handler acts only when its own token is last -- which is the
 * drawer mounted most recently, which is the one on top.
 *
 * The stack is module-level rather than React state on purpose: it is read
 * inside an event handler while the event is being dispatched, and state would
 * be a render behind.
 *
 * Nothing here handles the backdrop. That needs no coordination, because the
 * later drawer's own backdrop is physically over the earlier one and receives
 * the click -- see the panels, which pair it with `stopPropagation` so a click
 * inside the drawer is not a click outside it.
 */
import { useEffect } from 'react';

/**
 * Open drawers, oldest first. The last entry is the one on top.
 *
 * Exported with the three functions below so the ordering can be tested
 * without a DOM: the risk here is the bookkeeping, not the keypress, and
 * `@testing-library/react` is not a dependency of this project.
 */
const stack: symbol[] = [];

/** Register a newly opened drawer and return its token. */
export function pushDrawer(label = 'drawer'): symbol {
  const token = Symbol(label);
  stack.push(token);
  return token;
}

/**
 * Forget a closed drawer.
 *
 * By value rather than by popping the end: drawers usually close top-down but
 * nothing guarantees it, and a parent unmounting first would otherwise remove
 * its child's token and leave its own behind for ever. A leaked entry makes
 * every drawer opened afterwards believe something sits above it, and Escape
 * silently stops working.
 */
export function popDrawer(token: symbol): void {
  const at = stack.lastIndexOf(token);
  if (at !== -1) stack.splice(at, 1);
}

/** Whether this drawer is the one a keypress belongs to. */
export function isTopmostDrawer(token: symbol): boolean {
  return stack.length > 0 && stack[stack.length - 1] === token;
}

export function useDismissOnEscape(onDismiss: () => void, active = true): void {
  useEffect(() => {
    if (!active) return undefined;

    const token = pushDrawer();

    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape') return;
      if (!isTopmostDrawer(token)) return;
      // Stopped so one press closes one drawer. Without it a listener further
      // up -- another panel, a future global shortcut -- sees the same press
      // and acts on it too.
      event.stopPropagation();
      onDismiss();
    }

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      popDrawer(token);
    };
  }, [onDismiss, active]);
}
